/**
 * Engagement rules: what counts as having spoken to someone.
 *
 * Pure. No database, no Graph, no I/O. Imported by the calendar sync, the SQL
 * predicate builders, and the client surfaces, so all of them agree on the
 * definition by construction rather than by four hand-synced copies.
 *
 * This codebase has had five separate incidents of a duplicated constant
 * drifting one site at a time (model IDs, INACTIVE_STATUSES, the generic
 * email list, the outreach suppression check, and PERSONAL_EMAIL_DOMAINS
 * inlined in the email sync endpoint). The fix each time was consolidation
 * plus a static test. See server/engagementConsolidation.test.ts.
 */
import { isGenericInbox } from "./genericEmail";
import { parseGraphUtc } from "./graphTime";

/**
 * Activity types that mean a real conversation happened.
 *
 * 'email' is deliberately absent: an email is a weaker signal than a meeting,
 * and counting it would dilute what the checkmark means.
 */
export const ENGAGEMENT_ACTIVITY_TYPES = ["call", "meeting", "conference"] as const;
export type EngagementActivityType = (typeof ENGAGEMENT_ACTIVITY_TYPES)[number];

/** Our own team. Every calendar event contains us; none of us is a buyer contact. */
export const INTERNAL_EMAIL_DOMAINS = new Set(["firstturncapital.com"]);

/**
 * Free providers. Matching a company by one of these domains would attach
 * every Gmail user on earth to whichever company happened to have one Gmail
 * contact. Blocked for domain matching only: an exact email match against a
 * personal address is still valid and common for buyer principals.
 */
export const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
  "icloud.com", "me.com", "live.com", "msn.com", "mail.com", "protonmail.com",
]);

/** Lowercased domain part, or null when the input is not an address. */
export function emailDomain(email: string | null | undefined): string | null {
  const parts = (email ?? "").trim().toLowerCase().split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1] || !parts[1].includes(".")) return null;
  return parts[1];
}

/**
 * Accepted, tentative and no-response all count. Plenty of people join a call
 * without ever clicking Accept, so only an explicit decline is treated as
 * evidence of absence.
 */
export function attendeeCounts(response: string | null | undefined): boolean {
  return (response ?? "").toLowerCase() !== "declined";
}

/**
 * A calendar entry only proves a conversation once it has actually happened
 * and was not cancelled. A call booked for next Tuesday is not a conversation.
 */
export function eventQualifies(
  e: { isCancelled?: boolean; end?: { dateTime?: string | null; timeZone?: string | null } },
  now: Date,
): boolean {
  if (e.isCancelled) return false;
  // parseGraphUtc rather than new Date(): Graph's dateTime has no zone
  // designator, so new Date() would resolve it in the process timezone and
  // judge "has this ended yet" against a clock up to a day off.
  const end = parseGraphUtc(e.end);
  if (!end) return false;
  return end.getTime() < now.getTime();
}

/** Whether this address may be resolved to a CRM contact at all. */
export function attendeeIsEligible(email: string | null | undefined): boolean {
  const domain = emailDomain(email);
  if (!domain) return false;
  if (INTERNAL_EMAIL_DOMAINS.has(domain)) return false;
  if (isGenericInbox(email)) return false;
  return true;
}

/** Whether this domain may be used to infer a company (tier 2). */
export function isDomainMatchable(domain: string | null | undefined): boolean {
  if (!domain) return false;
  const d = domain.toLowerCase();
  return !PERSONAL_EMAIL_DOMAINS.has(d) && !INTERNAL_EMAIL_DOMAINS.has(d);
}
